import { formatPhoneNumberWithExtension } from "@/Shared/Components/formatPhoneNumberWithExtension";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { PencilSimpleIcon } from "@phosphor-icons/react/PencilSimple";
import type { User } from "@freightsimple/generated-dashboard-openapi-client/models/User";
import moment from "moment";
import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";
import { ViewCompanyChangeBranchDialog } from "../Dialogs/ViewCompanyChangeBranchDialog";
import type { ManageUserDialogDoneData } from "../Dialogs/ViewCompanyManageUserDialog";
import { ViewCompanyManageUserDialog } from "../Dialogs/ViewCompanyManageUserDialog";
import { viewCompanyDeletionBlockedReason } from "../Helpers/viewCompanyDeletionBlockedReason";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Table, type Column } from "@freightsimple/ui-core/Table/Table";
import { T } from "@freightsimple/ui-core/Typography/T";

interface BranchCellProps {
  user: User;
  setUsers: Dispatch<SetStateAction<User[]>>;
}

function BranchCell_(props: BranchCellProps) {
  const [changeBranchOpen, setChangeBranchOpen] = useState(false);
  const branchName = props.user.branchName;

  function onBranchChanged(updatedUser: User) {
    props.setUsers((users) => {
      const index = users.findIndex((u) => u.userId === updatedUser.userId);

      if (index === -1) {
        console.error("Couldn't find the user, skipping re-render");
        return users;
      }

      return users.with(index, updatedUser);
    });
  }

  return (
    <>
      <ViewCompanyChangeBranchDialog
        open={changeBranchOpen}
        onOpenChange={setChangeBranchOpen}
        user={props.user}
        onDone={onBranchChanged}
      />
      {/* Pencil sits on the same line as the branch name, centred against it —
          it used to stack underneath, which read as a stray button. */}
      <div className="flex items-center gap-2">
        {branchName === undefined ? (
          <T color="muted">Not assigned to branch</T>
        ) : (
          <span>{branchName}</span>
        )}
        {/* A plain Button, not `iconAction` — icon actions render with
            tabIndex={-1}, and this is the only way to change a branch, so it
            has to stay reachable from the keyboard. In a table it collapses to
            the icon with the label as its tooltip, or with disabledMessage as
            the tooltip when the branch isn't ours to change. */}
        {props.user.belongsToThisCompany ? (
          <Button
            icon={PencilSimpleIcon}
            onClick={() => setChangeBranchOpen(true)}
            data-testid="change-branch-button"
          >
            Change branch
          </Button>
        ) : (
          <Button
            icon={PencilSimpleIcon}
            disabled
            disabledMessage="This user belongs to a linked company — manage their branch from that company's account"
            data-testid="change-branch-button"
          >
            Change branch
          </Button>
        )}
      </div>
    </>
  );
}

function formatDate(date: Date): string {
  if (moment(date).diff(moment.now(), "days") < 7) {
    return moment(date).fromNow();
  } else {
    return moment(date).format("dddd Do MMMM YYYY h:mma z");
  }
}

interface ManageUserActionProps {
  user: User;
  setUsers: Dispatch<SetStateAction<User[]>>;
  /** Why this user can't be deleted, or undefined when deleting is allowed. */
  deletionBlockedReason: string | undefined;
}

function ManageUserAction_(props: ManageUserActionProps) {
  const [manageUserOpen, setManageUserOpen] = useState(false);

  function onManaged(data: ManageUserDialogDoneData) {
    const { updatedUser, type } = data;

    props.setUsers((users) => {
      const index = users.findIndex((u) => u.userId === updatedUser.userId);

      if (index === -1) {
        console.error("Couldn't find the user, skipping re-render");
        return users;
      }

      if (type === "delete") {
        return users.filter((_, i) => i !== index);
      }

      return users.with(index, updatedUser);
    });
  }

  return (
    <>
      <ViewCompanyManageUserDialog
        open={manageUserOpen}
        onOpenChange={setManageUserOpen}
        user={props.user}
        onFinished={onManaged}
        deletionBlockedReason={props.deletionBlockedReason}
      />
      {props.user.belongsToThisCompany ? (
        <Button size="link" onClick={() => setManageUserOpen(true)}>
          Manage User
        </Button>
      ) : (
        <Button
          size="link"
          disabled
          disabledMessage="This user belongs to a linked company — manage them from that company's account"
        >
          Manage User
        </Button>
      )}
    </>
  );
}

interface UsersInCompanySectionProps {
  users: User[];
  setUsers: Dispatch<SetStateAction<User[]>>;
}

function ViewCompanyUsersSection({
  users,
  setUsers,
}: UsersInCompanySectionProps) {
  const { user: signedInUser } = useAuthentication();

  const columns: Column<User>[] = [
    { key: "email", header: "Email", render: (u) => u.email },
    { key: "name", header: "Name", render: (u) => u.name },
    {
      key: "phone",
      header: "Phone",
      render: (u) => formatPhoneNumberWithExtension(u.phone, u.phoneExtension),
    },
    {
      key: "branch",
      header: "Branch Location",
      render: (u) => <BranchCell_ user={u} setUsers={setUsers} />,
    },
    {
      key: "lastSignin",
      header: "Last Signin",
      render: (u) => formatDate(u.lastLoginAt),
    },
  ];

  if (users.length === 0) {
    return <></>;
  }

  return (
    <Table
      tableId="company-users"
      columns={columns}
      data={users}
      rowKey={(u) => u.userId}
      rowActions={(u) => (
        <ManageUserAction_
          user={u}
          setUsers={setUsers}
          deletionBlockedReason={viewCompanyDeletionBlockedReason({
            user: u,
            users,
            signedInUserId: signedInUser?.userId,
          })}
        />
      )}
    />
  );
}
export default ViewCompanyUsersSection;
